import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import {
  AppListingPageSkeleton,
  getSelectedAppVersion,
  isRuByPartnerAppAccessDeniedError,
  useAppListingById,
  useGetAppDetailsFromSearch,
} from '@atlassian/mpac-app-listing';
import { useStatsig } from '@atlassian/mpac-feature-flags';
import {
  AtlassianSupportsUkraineRouteHandler,
  FluidContainer,
  OutageError,
} from '@atlassian/mpac-primitives';
import { HostingTypeToFiltersQueryParam, HostingTypeToQueryParam } from '@atlassian/mpac-types';
import {
  MarketplaceEntityStatus,
  MarketplaceLocation,
} from '@atlassian/mpac-types/graphql-gateway-types';
import {
  RedirectExternal,
  RedirectToLogin,
  useHistory,
  useLocationHref,
  usePageRouteSourceProvider,
} from '@atlassian/mpac-ui';
import {
  archivedAppLink,
  encodeAppName,
  log,
  UrlUtils,
  useAlgoliaConfig,
} from '@atlassian/mpac-utils';
import { css } from '@compiled/react';
import capitalize from 'lodash/capitalize';
import { useEffect, useRef, useState } from 'react';

import { safeToForwardToPartner, trackTabChange } from 'common/utils/analytics/analytics';
import SEOFriendlyRedirect from '../../../../../../../../common/components/SEOFriendlyRedirect';
import BootstrapConfig from '../../../../../../../bootstrapConfig';
import { AppListingPageTask } from '../../../../../../../telemetry/tasks';
import NotFoundPage from '../../../../../../pages/errors/NotFoundPage';
import { getFirstValidHostingOption, toAnalyticsHosting } from '../utils';
import AppListingPageContent from './AppListingPageContent';
import { makeLinkTemplates } from './utils';

import type { ApolloError } from '@apollo/client';
import type { AppReviewSummaryDetails } from '@atlassian/mpac-app-listing';
import type { StatsigClientType } from '@atlassian/mpac-feature-flags';
import type {
  AppListingTabsType,
  HostingWithCloudComplianceBoundariesType,
} from '@atlassian/mpac-types';

const { updateQueryParam } = UrlUtils;

type ReviewSummaryState = AppReviewSummaryDetails | undefined;

type AppListingPageProps = {
  appId: string;
  selectedHosting: HostingWithCloudComplianceBoundariesType | null | undefined;
  tab: AppListingTabsType;
  showOnlyPublic: boolean;
};

const aetherErrorContainer = css({
  minHeight: 'calc(100vh - 152px)',
});

const getNotFoundPage = () => {
  return (
    <div css={aetherErrorContainer}>
      <NotFoundPage />
    </div>
  );
};

// eslint-disable-next-line max-lines-per-function
const AppListingPage = (props: AppListingPageProps) => {
  const { appId, selectedHosting, showOnlyPublic } = props;
  const history = useHistory();
  const locationHref = useLocationHref();
  const links = makeLinkTemplates();
  const mpacBaseUrl = BootstrapConfig.get().mpacBaseUrl;
  const { enableCanonicalUrlRedirectionHandler } = useStatsig() as StatsigClientType;
  const isCanonicalUrlRedirectionHandlerEnabled = enableCanonicalUrlRedirectionHandler();
  const { pageRouteSource, setPageRouteSource } = usePageRouteSourceProvider();
  const [capturedRouteSource, setCapturedRouteSource] = useState<string | null>(null);

  const setNewHostingType = (newHosting: HostingWithCloudComplianceBoundariesType) => {
    const updatedPathname = updateQueryParam(
      locationHref,
      'hosting',
      HostingTypeToQueryParam[newHosting]
    );

    // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
    history.replace(updatedPathname);
    safeToForwardToPartner(appId, () => {
      trackTabChange(updatedPathname, toAnalyticsHosting(newHosting), true);
    });
  };

  const setNewPageTab = (tab: AppListingTabsType) => {
    const newPath = updateQueryParam(locationHref, 'tab', tab);
    // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
    history.replace(newPath);
    safeToForwardToPartner(appId, () => {
      trackTabChange(newPath, capitalize(tab), true);
    });
  };

  // run this effect before any other hooks
  useEffect(() => {
    AppListingPageTask.start();
  }, []);

  // Log the page route source when it changes
  useEffect(() => {
    if (pageRouteSource) {
      setCapturedRouteSource(pageRouteSource);
      setPageRouteSource(null);
    }
  }, [pageRouteSource, setPageRouteSource]);

  const { data, error, loading } = useAppListingById({
    appId,
    location: MarketplaceLocation.Website,
    showOnlyPublic,
  });

  const marketplaceApp = data?.marketplaceApp;
  const editions = data?.marketplaceStore?.editions;

  // State for the initial review summary
  const [initialReviewSummary, setInitialReviewSummary] = useState<ReviewSummaryState>(undefined);
  const hasSetInitialSummary = useRef(false);

  useEffect(() => {
    if (data?.marketplaceStore?.appReviewsByAppId && !hasSetInitialSummary.current) {
      // to avoid re-rendering the component when the review apollo cache is updated from reviews tab
      setInitialReviewSummary(data.marketplaceStore.appReviewsByAppId);
      hasSetInitialSummary.current = true;
    }
  }, [data]);

  const algoliaConfig = useAlgoliaConfig();
  const appDetailsFromSearchResponse = useGetAppDetailsFromSearch({ appId, algoliaConfig });

  if (error) {
    if (marketplaceApp?.entityStatus === MarketplaceEntityStatus.Archived) {
      return redirectArchivedApp(mpacBaseUrl, appId);
    }
    return handleApolloError(error, locationHref);
  }

  if (isCanonicalUrlRedirectionHandlerEnabled && appDetailsFromSearchResponse?.appData) {
    // Extract app slug from URL path
    const urlPathParts = locationHref.split('/');
    const urlAppSlug = urlPathParts[urlPathParts.length - 1].split('?')[0];
    const searchResponseAppSlug = encodeAppName(appDetailsFromSearchResponse.appData.name);
    if (searchResponseAppSlug && urlAppSlug !== searchResponseAppSlug) {
      const currentQueryParams = new URLSearchParams(locationHref.split('?')[1] || '');

      const finalUrl = `/apps/${props.appId}/${searchResponseAppSlug}${
        currentQueryParams.toString() ? `?${currentQueryParams.toString()}` : ''
      }`;

      AppListingPageTask.abort();
      return <SEOFriendlyRedirect to={finalUrl} statusCode={301} />;
    }
  }

  const resolveAppListingContent = () => {
    const notFound = getNotFoundPage();
    if (!marketplaceApp) {
      // return null -> actually loading and waiting for response. the spinner would be shown
      // return notFound -> if not loading and still data is false, it means this app does not exist
      return loading ? null : notFound;
    }

    if (marketplaceApp.entityStatus === MarketplaceEntityStatus.Archived) {
      return redirectArchivedApp(mpacBaseUrl, appId);
    }

    const firstValidHosting: HostingWithCloudComplianceBoundariesType =
      selectedHosting || getFirstValidHostingOption(marketplaceApp);

    if (!isCanonicalUrlRedirectionHandlerEnabled && firstValidHosting !== selectedHosting) {
      setNewHostingType(firstValidHosting);
    }

    const selectedAppVersion = getSelectedAppVersion(firstValidHosting, marketplaceApp);

    if (!selectedAppVersion) {
      if (marketplaceApp && marketplaceApp.allServerDCAppVersions.totalCount > 0) {
        const versionHistoryUrl = links.appVersionHistory.expand({
          appId: props.appId,
          appSlug: marketplaceApp.slug,
          hosting: HostingTypeToFiltersQueryParam[firstValidHosting],
        });
        AppListingPageTask.abort();
        return <RedirectExternal statusCode={302} url={versionHistoryUrl} />;
      }

      return notFound;
    }

    return (
      <AppListingPageContent
        appDetails={marketplaceApp}
        editions={editions}
        reviewSummaryDetails={initialReviewSummary}
        appDetailsFromSearchResponse={appDetailsFromSearchResponse}
        appId={props.appId}
        tab={props.tab}
        links={links}
        selectedHosting={firstValidHosting}
        selectedAppVersion={selectedAppVersion}
        setNewHostingType={setNewHostingType}
        setNewTab={setNewPageTab}
        showOnlyPublic={showOnlyPublic}
        pageRouteSource={capturedRouteSource}
      />
    );
  };

  // Show a spinner only when we don't have data
  // from the graphql cache available immediately
  const showSpinner = !marketplaceApp && loading;
  const PageContainer = FluidContainer;

  if (showSpinner) {
    return (
      <UFOLoadHold name="applistingscreen">
        <PageContainer>
          <AppListingPageSkeleton selectedHosting={selectedHosting} />
        </PageContainer>
      </UFOLoadHold>
    );
  }

  return <PageContainer>{resolveAppListingContent()}</PageContainer>;
};

const redirectArchivedApp = (mpacBaseUrl, appId) => {
  const archivedAppUrl = archivedAppLink(mpacBaseUrl).expand({ appId });
  log.info('AppListingPage', `App is archived, redirecting to archived app url, ${archivedAppUrl}`);
  AppListingPageTask.abort();
  return <RedirectExternal statusCode={302} url={archivedAppUrl} />;
};

const handleApolloError = (error: ApolloError, locationHref: string) => {
  /**
   * When a logged-in users' session expires, the gateway treats it as failed-auth instead of
   * downgrading it to an unauthenticated call. Temporarily, we will redirect 401 responses
   * to the login page, for the app listing page.
   * See https://ecosystem.atlassian.net/browse/MC-1770
   */
  if (
    error.networkError &&
    'statusCode' in error.networkError &&
    error.networkError.statusCode === 401
  ) {
    log.info('AppListingPage', 'Auth failure while fetching app listing, redirecting to login');
    AppListingPageTask.abort();
    return <RedirectToLogin />;
  }

  if (isRuByPartnerAppAccessDeniedError(error)) {
    log.info('AppListingPage', `Prevented App Listing Access to RuBy partner App, ${locationHref}`);
    AppListingPageTask.abort();
    return <AtlassianSupportsUkraineRouteHandler />;
  }

  return (
    <div css={aetherErrorContainer}>
      <OutageError />
    </div>
  );
};

export default AppListingPage;
